import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IKillsFilterOptions } from '@app/interfaces/kill';
import { getUpdatedOptions } from '@app/utils/pageFilters';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IKillsFetchParams } from '@app/api/kills.api';

interface KillsFilterState {
  filterOptions: IKillsFilterOptions | null;
  shouldTriggerFetch: boolean;
  // Applied (last fetched)
  selectedAnimalIds: number[];
  selectedKillStatuses: string[];
  selectedGender: string | null;
  selectedAge: number | null;
  misfire: boolean | null;
  valorization: boolean | null;
  searchText: string;
  fromFilter: string | null;
  toFilter: string | null;
  onlyKills: boolean;
  gamekeeperKill: boolean;
  selectedKillType: string | null;
  selectedReasonKill: string | null;
  selectedCauseOfDeathTypeId: number | null;
  // Temporal (panel in-flight)
  selectedAnimalIdsTemp: number[];
  selectedKillStatusesTemp: string[];
  selectedGenderTemp: string | null;
  selectedAgeTemp: number | null;
  misfireTemp: boolean | null;
  valorizationTemp: boolean | null;
}

const initialState: KillsFilterState = {
  filterOptions: null,
  shouldTriggerFetch: false,
  selectedAnimalIds: [],
  selectedKillStatuses: [],
  selectedGender: null,
  selectedAge: null,
  misfire: null,
  valorization: null,
  searchText: '',
  fromFilter: null,
  toFilter: null,
  onlyKills: false,
  gamekeeperKill: false,
  selectedKillType: null,
  selectedReasonKill: null,
  selectedCauseOfDeathTypeId: null,
  selectedAnimalIdsTemp: [],
  selectedKillStatusesTemp: [],
  selectedGenderTemp: null,
  selectedAgeTemp: null,
  misfireTemp: null,
  valorizationTemp: null,
};

const killsFiltersSlice = createSlice({
  name: 'killsFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<IKillsFilterOptions>) {
      state.filterOptions = action.payload;

      const availableAnimalIds = getUpdatedOptions(
        state.selectedAnimalIdsTemp as FilterIdsType,
        action.payload.uniqueAnimals,
      );
      state.selectedAnimalIdsTemp = availableAnimalIds as number[];
      state.selectedAnimalIds = availableAnimalIds as number[];
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setSelectedAnimalIds(state, action: PayloadAction<number[]>) {
      state.selectedAnimalIds = action.payload;
    },
    setSelectedAnimalIdsTemp(state, action: PayloadAction<number[]>) {
      state.selectedAnimalIdsTemp = action.payload;
    },
    setSelectedKillStatuses(state, action: PayloadAction<string[]>) {
      state.selectedKillStatuses = action.payload;
    },
    setSelectedKillStatusesTemp(state, action: PayloadAction<string[]>) {
      state.selectedKillStatusesTemp = action.payload;
    },
    setSelectedGender(state, action: PayloadAction<string | null>) {
      state.selectedGender = action.payload;
      state.selectedGenderTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedGenderTemp(state, action: PayloadAction<string | null>) {
      state.selectedGenderTemp = action.payload;
    },
    setSelectedAge(state, action: PayloadAction<number | null>) {
      state.selectedAge = action.payload;
      state.selectedAgeTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedAgeTemp(state, action: PayloadAction<number | null>) {
      state.selectedAgeTemp = action.payload;
    },
    setMisfire(state, action: PayloadAction<boolean | null>) {
      state.misfire = action.payload;
      state.misfireTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setMisfireTemp(state, action: PayloadAction<boolean | null>) {
      state.misfireTemp = action.payload;
    },
    setValorization(state, action: PayloadAction<boolean | null>) {
      state.valorization = action.payload;
      state.valorizationTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setValorizationTemp(state, action: PayloadAction<boolean | null>) {
      state.valorizationTemp = action.payload;
    },
    setFromFilter(state, action: PayloadAction<string | null>) {
      state.fromFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToFilter(state, action: PayloadAction<string | null>) {
      state.toFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyKills(state, action: PayloadAction<boolean>) {
      state.onlyKills = action.payload;
      state.shouldTriggerFetch = true;
    },
    setGamekeeperKill(state, action: PayloadAction<boolean>) {
      state.gamekeeperKill = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedKillType(state, action: PayloadAction<string | null>) {
      state.selectedKillType = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedReasonKill(state, action: PayloadAction<string | null>) {
      state.selectedReasonKill = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedCauseOfDeathTypeId(state, action: PayloadAction<number | null>) {
      state.selectedCauseOfDeathTypeId = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.searchText = initialState.searchText;
      state.selectedAnimalIds = initialState.selectedAnimalIds;
      state.selectedKillStatuses = initialState.selectedKillStatuses;
      state.selectedGender = initialState.selectedGender;
      state.selectedAge = initialState.selectedAge;
      state.misfire = initialState.misfire;
      state.valorization = initialState.valorization;
      state.fromFilter = initialState.fromFilter;
      state.toFilter = initialState.toFilter;
      state.selectedAnimalIdsTemp = initialState.selectedAnimalIdsTemp;
      state.selectedKillStatusesTemp = initialState.selectedKillStatusesTemp;
      state.selectedGenderTemp = initialState.selectedGenderTemp;
      state.selectedAgeTemp = initialState.selectedAgeTemp;
      state.misfireTemp = initialState.misfireTemp;
      state.valorizationTemp = initialState.valorizationTemp;
      state.onlyKills = initialState.onlyKills;
      state.gamekeeperKill = initialState.gamekeeperKill;
      state.selectedKillType = initialState.selectedKillType;
      state.selectedReasonKill = initialState.selectedReasonKill;
      state.selectedCauseOfDeathTypeId = initialState.selectedCauseOfDeathTypeId;
      state.shouldTriggerFetch = true;
    },
  },
});

export const killsFiltersActions = killsFiltersSlice.actions;

export const fetchParamsSelector = (state: { killsFilters: KillsFilterState }): IKillsFetchParams => {
  const {
    searchText,
    selectedAnimalIds,
    selectedKillStatuses,
    selectedGender,
    selectedAge,
    misfire,
    valorization,
    fromFilter,
    toFilter,
    onlyKills,
    gamekeeperKill,
    selectedKillType,
    selectedReasonKill,
    selectedCauseOfDeathTypeId,
  } = state.killsFilters;

  return {
    user: searchText || undefined,
    animalIds: selectedAnimalIds.length ? selectedAnimalIds : undefined,
    killstatus: selectedKillStatuses.length ? selectedKillStatuses : undefined,
    gender: selectedGender || undefined,
    ageFrom: selectedAge ?? undefined,
    ageTo: selectedAge ?? undefined,
    misfire: misfire ?? undefined,
    valorization: valorization ?? undefined,
    from: fromFilter || undefined,
    to: toFilter || undefined,
    onlyKills: onlyKills ? 1 : undefined,
    gamekeeperKill: gamekeeperKill ? true : undefined,
    killType: selectedKillType || undefined,
    reasonKill: selectedReasonKill || undefined,
    causeOfDeathTypeId: selectedCauseOfDeathTypeId ?? undefined,
  };
};

export const areFiltersAppliedSelector = (state: { killsFilters: KillsFilterState }): boolean => {
  const {
    searchText,
    selectedAnimalIds,
    selectedKillStatuses,
    selectedGender,
    selectedAge,
    misfire,
    valorization,
    fromFilter,
    toFilter,
    onlyKills,
    gamekeeperKill,
    selectedKillType,
    selectedReasonKill,
    selectedCauseOfDeathTypeId,
  } = state.killsFilters;

  return (
    !!searchText.length ||
    !!selectedAnimalIds.length ||
    !!selectedKillStatuses.length ||
    selectedGender !== null ||
    selectedAge !== null ||
    misfire !== null ||
    valorization !== null ||
    !!fromFilter ||
    !!toFilter ||
    onlyKills ||
    gamekeeperKill ||
    selectedKillType !== null ||
    selectedReasonKill !== null ||
    selectedCauseOfDeathTypeId !== null
  );
};

export const activeFiltersCountSelector = (state: { killsFilters: KillsFilterState }): number => {
  const {
    searchText,
    selectedAnimalIds,
    selectedKillStatuses,
    selectedGender,
    selectedAge,
    misfire,
    valorization,
    fromFilter,
    toFilter,
    selectedReasonKill,
    selectedCauseOfDeathTypeId,
  } = state.killsFilters;

  let count = 0;
  if (searchText.length > 0) count++;
  if (selectedAnimalIds.length > 0) count++;
  if (selectedKillStatuses.length > 0) count++;
  if (selectedGender !== null) count++;
  if (selectedAge !== null) count++;
  if (misfire !== null) count++;
  if (valorization !== null) count++;
  if (fromFilter) count++;
  if (toFilter) count++;
  if (selectedReasonKill !== null) count++;
  if (selectedCauseOfDeathTypeId !== null) count++;

  return count;
};

export const filterOptionsSelector = (state: { killsFilters: KillsFilterState }) => state.killsFilters.filterOptions;

export const killsFilters = killsFiltersSlice.reducer;
